import { useState, useEffect } from "react";

export type Lead = {
  id:                  string;
  tenant_id:           string;
  name:                string;
  email:               string | null;
  phone:               string | null;
  event_type:          string | null;
  event_date:          string | null;
  message:             string | null;
  status:              string;
  source:              string | null;
  converted_client_id: string | null;
  internal_notes:      string | null;
  updated_at:          string | null;
  created_at:          string;
};

const EVENT_TYPES = [
  "Wedding", "XV Años", "Baptism", "Graduation", "Birthday",
  "Baby Shower", "Engagement", "Anniversary", "Corporate Event", "Portrait", "Other",
];

const STATUSES = [
  { value: "new",       label: "New",       color: "bg-sky-100 text-sky-700 border-sky-200" },
  { value: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "booked",    label: "Booked",    color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "lost",      label: "Lost",      color: "bg-red-100 text-red-600 border-red-200" },
];

interface Props {
  lead?: Lead | null;
  onClose: () => void;
  onSave: (data: Partial<Lead>) => Promise<void>;
}

const INPUT = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all";

export default function LeadModal({ lead, onClose, onSave }: Props) {
  const isCreate = !lead;
  const [form, setForm] = useState({
    name:       lead?.name       ?? "",
    email:      lead?.email      ?? "",
    phone:      lead?.phone      ?? "",
    event_type: lead?.event_type ?? "",
    event_date: lead?.event_date ?? "",
    message:    lead?.message    ?? "",
    status:     lead?.status     ?? "new",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const set = (k: string, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => { const n = { ...p }; delete n[k]; return n; });
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    await onSave({
      name:       form.name.trim(),
      email:      form.email.trim()      || null,
      phone:      form.phone.trim()      || null,
      event_type: form.event_type.trim() || null,
      event_date: form.event_date        || null,
      message:    form.message.trim()    || null,
      status:     form.status,
      source:     isCreate ? 'manual' : undefined,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-slate-200 w-full max-w-lg shadow-xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className={`${isCreate ? 'ri-user-add-line' : 'ri-pencil-line'} text-[#C8A96E] text-sm`}></i>
            </div>
            <div>
              <h2 className="text-slate-800 font-semibold text-sm">{isCreate ? 'New Manual Lead' : 'Edit Lead'}</h2>
              <p className="text-slate-400 text-xs mt-0.5">{isCreate ? 'Register a prospect by phone or referral' : 'Update lead information'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">

          {/* Name */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className={`${INPUT} ${errors.name ? "border-red-300" : ""}`}
              placeholder="Lead name"
              autoFocus
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Email + Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={`${INPUT} ${errors.email ? "border-red-300" : ""}`}
                placeholder="email@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className={INPUT}
                placeholder="+1 555 000 0000"
              />
            </div>
          </div>

          {/* Event Type + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Type</label>
              <select
                value={form.event_type}
                onChange={(e) => set("event_type", e.target.value)}
                className={`${INPUT} cursor-pointer`}
              >
                <option value="">— Select —</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Date</label>
              <input
                type="date"
                value={form.event_date}
                onChange={(e) => set("event_date", e.target.value)}
                className={`${INPUT} cursor-pointer`}
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2 block">Status</label>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => set("status", s.value)}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                    form.status === s.value
                      ? s.color
                      : "bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300 hover:text-slate-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider">Message / Notes</label>
              <span className={`text-[10px] font-mono ${form.message.length > 450 ? "text-amber-500" : "text-slate-300"}`}>
                {form.message.length}/500
              </span>
            </div>
            <textarea
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={3}
              maxLength={500}
              className={`${INPUT} resize-none`}
              placeholder="Lead message or internal notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isCreate ? (
                <><i className="ri-user-add-line"></i> Add Lead</>
              ) : (
                <><i className="ri-save-line"></i> Save Changes</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}